import { memo, useContext } from 'react';
import { StrategyApyRisks } from '../../../../context/app/appTypes';
import { formatPercentages } from '../../../../helpers/formats';
import BarChart from '../../../BarChart';
import CollapsibleArea from '../../../CollapsibleArea';
import InfoPopUp from '../../../InfoPopUp';
import { Table, TableRow, TableRowContainer } from '../../../Table';
import { ErrorIcon } from '../../../../assets/icons';
import appContext from '../../../../context/app/appContext';

const poolHeaders = [
    '#',
    'Strategy',
    'APY',
    'Allocation',
    <div>
        <span>Risk Rating</span>
        <InfoPopUp>
            The lower the score assigned by the Risk Model, the lower the associated risk
            with the strategy.
        </InfoPopUp>
    </div>,
];

type Props = {
    strategyAllocationDetails: StrategyApyRisks;
    collapsed: boolean;
};

const CapitalAllocation: React.FC<Props> = ({ strategyAllocationDetails, collapsed }) => {
    const { chainStrategies } = useContext(appContext);

    const highestAllocation = Object.values(strategyAllocationDetails).reduce(
        (largest, { allocation }) => (+allocation > largest ? +allocation : largest),
        0
    );

    return (
        <CollapsibleArea collapsed={collapsed}>
            <Table
                dataLength={Object.keys(strategyAllocationDetails || {}).length || 0}
                numOfColumns={poolHeaders.length}
                headers={poolHeaders}
            >
                <TableRowContainer>
                    {Object.entries(strategyAllocationDetails)
                        .sort(([_, a], [__, b]) => +b.allocation - +a.allocation)
                        .map(([strategyId, { allocation, apy, risk }], index) => (
                            <TableRow className='collapsed' key={strategyId}>
                                <div>{index + 1}</div>
                                <div>
                                    <span>
                                        {chainStrategies[strategyId.toLowerCase()]
                                            ?.name ?? 'Strategy'}
                                    </span>
                                    {chainStrategies[strategyId.toLowerCase()]
                                        ?.negativeApyText && (
                                        <InfoPopUp
                                            trigger={<ErrorIcon />}
                                            className='warning-pop-up'
                                        >
                                            {
                                                chainStrategies[strategyId.toLowerCase()]
                                                    ?.negativeApyText
                                            }
                                        </InfoPopUp>
                                    )}
                                    <InfoPopUp>
                                        {chainStrategies[strategyId.toLowerCase()]
                                            ?.detail ?? 'Strategy Detail'}
                                    </InfoPopUp>
                                </div>
                                <div>
                                    {formatPercentages(parseFloat(apy) * 100 || 0)}%
                                    <span className='column-label'>{poolHeaders[2]}</span>
                                </div>
                                <div>
                                    <BarChart
                                        value={+formatPercentages(allocation || 0)}
                                        total={highestAllocation}
                                        isCounting={collapsed}
                                        text={`${+formatPercentages(allocation || 0)}%`}
                                    />
                                    <span className='column-label'>{poolHeaders[3]}</span>
                                </div>
                                <div>
                                    {formatPercentages(risk || 0, 1)}
                                    <span className='column-label'>{poolHeaders[4]}</span>
                                </div>
                            </TableRow>
                        ))}
                </TableRowContainer>
            </Table>
        </CollapsibleArea>
    );
};

export default memo(CapitalAllocation);
